import type { AnyFieldApi } from '@tanstack/react-form';
import type { ReactNode } from 'react';
import { Field, Textarea, type FieldLayout, type TextareaProps } from '@/shared/ui';

export interface TextareaFieldProps extends Omit<
  TextareaProps,
  'value' | 'onChange' | 'onBlur' | 'invalid'
> {
  /** TanStack Form field API (z render-prop `form.Field`). */
  field: AnyFieldApi;
  label?: ReactNode;
  helperText?: ReactNode;
  /** Rozložení labelu (default `'inline'` — label vlevo). */
  layout?: FieldLayout;
  /** Šířka levého labelu v px (jen `layout="inline"`). */
  labelWidth?: number;
}

/**
 * Adapter mezi TanStack Form polem a Phoenix `Textarea`. Chybu zobrazuje až po dotčení pole
 * (`isTouched`), obalen do `Field` (label + hint/error) — analogicky k `TextField`.
 */
export function TextareaField({
  field,
  label,
  helperText,
  required,
  layout = 'inline',
  labelWidth,
  ...props
}: TextareaFieldProps) {
  const error = field.state.meta.isTouched
    ? (field.state.meta.errors[0]?.message as string | undefined)
    : undefined;

  return (
    <Field
      label={label}
      required={required}
      error={error}
      hint={helperText}
      layout={layout}
      labelWidth={labelWidth}
    >
      <Textarea
        value={(field.state.value as string | undefined) ?? ''}
        onChange={(event) => field.handleChange(event.target.value)}
        onBlur={field.handleBlur}
        required={required}
        invalid={Boolean(error)}
        {...props}
      />
    </Field>
  );
}
